import { createAsyncThunk } from '@reduxjs/toolkit';
import { Interaction } from '@constants';
import { BiToastType } from '@services/BiLogger';
import { toastsActions } from '../../toast';
import { modalsActions } from '../../modal';
import { type ThunkApiConfig } from '../../store.types';
import { getLastUpdatedMethod } from '../selectors';
import {
  fetchPaymentMethodTypes,
  fetchSavedPaymentMethods,
  savedPaymentMethodsActions,
} from '..';
import { type RemoveThunk } from './types';

export const removeSavedPaymentMethod = createAsyncThunk<
  void,
  RemoveThunk,
  ThunkApiConfig
>(
  'savedPaymentMethods/remove',
  async (
    { paymentMethodId },
    { extra, dispatch, getState, rejectWithValue },
  ) => {
    const { flowAPI, savedPaymentMethodsApi } = extra;
    const { environment, errorHandler, translations, panoramaClient } = flowAPI;

    try {
      const state = getState();

      if (environment.isViewer) {
        await savedPaymentMethodsApi.remove(paymentMethodId);

        const lastUpdatedMethod = getLastUpdatedMethod(paymentMethodId)(state);
        if (lastUpdatedMethod) {
          dispatch(
            savedPaymentMethodsActions.setMethodPrimary(lastUpdatedMethod.id),
          );
        }

        await dispatch(fetchSavedPaymentMethods());

        dispatch(fetchPaymentMethodTypes());
      }

      panoramaClient.transaction(Interaction.RemovePaymentMethod).finish();
      dispatch(modalsActions.closeModal());
      dispatch(
        toastsActions.showToast({
          text: translations.t('app.toast.payment-method-removed.text'),
          meta: { action: BiToastType.RemovedPaymentMethod },
        }),
      );
    } catch (error) {
      const resolvedError = errorHandler.getResolvedError(error);

      dispatch(
        toastsActions.showToast({
          text: resolvedError.message,
          meta: { action: BiToastType.RemoveError, skin: 'error' },
        }),
      );

      if (error instanceof Error) {
        panoramaClient
          .logger()
          .error(Interaction.RemovePaymentMethod, { error });
        panoramaClient.errorMonitor().reportError(error);
      }

      throw rejectWithValue({ error });
    }
  },
);
